import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import { CONSTANTS } from "@data"
import { css, logTool, featureFlag, debounce, listenDocEvent } from "@jsUtils"
import { getSafeDataMap } from "@jsUtils/contentful"
import { Meta, BubbleWrap, FadeMounter } from "@shared"
import {
  Banner,
  Footer,
  Header,
  HeroGrid,
  News,
  Subscribe,
  Spotlight,
  EmailModal,
  AlertBanner
} from "@components/sections"

import Styles from "@pageStyles/index.module.scss"

const { PAGE_NAMES, THEMES, COLORS, EVENTS, PAGE_TRACKER_KEYS, PAGE_TRACKER_VALUES } = CONSTANTS

const log = logTool()

// do we want to remount the bubbles on resize?
const USE_BUBBLE_REMOUNT = true
const USE_BUBBLE_ANI = featureFlag("bubblesMove")

/**
 * Home Page
 * @param {object} props - react props
 */
const IndexPage = props => {
  const { data } = props
  const {
    contentfulPageMeta,
    contentfulHome,
    contentfulCrmEventBasedModal,
    contentfulNewsAlertBanner
  } = data
  const { productSpotlight, news, banner, bottomBanner } = contentfulHome
  const { isModalEnabledOnSite } = contentfulCrmEventBasedModal
  const { isAlertEnabledOnSite } = contentfulNewsAlertBanner
  // for handling mount/remount of bubbles on resize
  const [showBubbles, setBubbles] = useState(true)

  log("Home page content", { contentfulPageMeta, contentfulHome })

  const productSpotlights = getSafeDataMap(productSpotlight, spotlightData => {
    return <Spotlight isTM data={spotlightData} key={spotlightData.title} />
  })

  const reBubbleMount = () => {
    setBubbles(!showBubbles)
  }

  // this forces the bubbles to remount on resize
  // to properly update their position
  // issue being solved is that when we resize, the
  // bubbles' position does not update correctly since it
  // is different after the animation.
  //  start position -> finish position matters
  //  resize position != start position
  const onResize = debounce(
    () => {
      setBubbles(!showBubbles)
    },
    this,
    500
  )

  useEffect(() => {
    if (USE_BUBBLE_REMOUNT && USE_BUBBLE_ANI) {
      log("Hero - bind events")
      listenDocEvent(EVENTS.RESIZE, onResize)
      return () => {
        listenDocEvent(EVENTS.RESIZE, onResize, false)
      }
    }
    return () => {}
  }, [])

  return (
    <>
      <Meta
        base={contentfulPageMeta}
        page={contentfulHome}
        hubSpotObj={{
          contentTypeKey: PAGE_TRACKER_KEYS.HOMEPAGE,
          contentTypeValue: PAGE_TRACKER_VALUES.WEBSITE_PAGE
        }}
      />
      <meta property="og:title" content="Ultrasound Workflow | Exo Works" />
      <Header theme={THEMES.DARK} pageColor={COLORS.DARK} />
      {/* <SubscribeForm  theme={THEMES.DARK} color={COLORS.FLURO} /> */}
      {/* <BannerHome /> */}
      <HeroGrid
        largeBaseHeaderSection
        theme={THEMES.DARK}
        page={PAGE_NAMES.HOMEPAGE}
        color={COLORS.FLURO}
        content={contentfulHome}
        classes={{
          headerText: Styles.homePageHeading,
          extraHeroWrapperRow: Styles.homeHeroWrapperRow,
          extraPageHeadingContent: Styles.homeExtraPageHeadingContent,
          subhead: Styles.homeSubhead
        }}
        analytics={{
          ascope: "CTAS",
          adata: "HERO.EXPLORE"
        }}
      >
        <div className={Styles.bubbleWrapper}>
          <FadeMounter showing={showBubbles} unMountedCallback={reBubbleMount}>
            <div className={css(Styles.bubbles, "bubblesIEOverride")}>
              <BubbleWrap animate={USE_BUBBLE_ANI} />
            </div>
          </FadeMounter>

          {/* <Button  className={Styles.subscribeButton}>Subscribe To Newsletter</Button> */}
        </div>
      </HeroGrid>
      <Banner data={banner} />
      {productSpotlights}
      <Banner data={bottomBanner} />
      <News color={COLORS.FLURO} data={news} />
      <Subscribe theme={THEMES.DARK} color={COLORS.FLURO} />
      {isModalEnabledOnSite ? (
        <EmailModal data={contentfulCrmEventBasedModal} theme={THEMES.DARK} color={COLORS.FLURO} />
      ) : null}
      {isAlertEnabledOnSite ? (
        <AlertBanner data={contentfulNewsAlertBanner} theme={THEMES.LIGHT} />
      ) : null}
      <Footer />
    </>
  )
}

/**
 * pageQuery for Home page
 * @todo is there a way to make the global data a fragment?
 */
export const pageQuery = graphql`
  {
    contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    contentfulCrmEventBasedModal {
      heroImage {
        title
        fluid(quality: 100, maxWidth: 1920) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      supportingText
      heroText
      isModalEnabledOnSite
    }
    contentfulNewsAlertBanner {
      bodyCopy
      ctaText
      ctaLink
      isAlertEnabledOnSite
    }
    contentfulHome {
      slug
      pageMeta {
        ...PageMetaData
      }
      headline
      description
      ctaLabel
      ctaLink
      banner {
        ...BannerSection
      }
      productSpotlight {
        ...ProductSpotlight
      }
      bottomBanner {
        ...BannerSection
      }
      news {
        ...NewsSection
        source
      }
    }
  }
`

export default IndexPage
